import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { applyNonceToElement } from 'core/utils/csp-nonce';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public currentTheme: string;
  public currentSiteId: string;

  constructor(@Inject(DOCUMENT) private document: Document, private overlayContainer: OverlayContainer) {}

  public setUpTheme(siteConfig: ISiteSettings) {
    // remove the old theme if one is present
    if (this.currentTheme) {
      this.document.documentElement.classList.remove(this.currentTheme);
    }

    if (this.currentSiteId) {
      this.document.documentElement.classList.remove(this.currentSiteId);
    }

    const siteName = siteConfig.fe_configuration.path;
    const countryCode = siteConfig.country_code;

    if (this.document.documentElement.classList.contains(countryCode)) {
      this.document.documentElement.classList.remove(countryCode);
    }

    this.currentTheme = `${siteName}-theme`;
    this.currentSiteId = `siteid-${siteConfig.id}`;
    // adds the parent class to the html tag so the correct clients material theme is applied
    this.document.documentElement.classList.add(this.currentTheme);
    this.document.documentElement.classList.add(this.currentSiteId);
    this.document.documentElement.classList.add(countryCode);
    // needed for material to apply styles to the overlays
    this.overlayContainer.getContainerElement().classList.add(this.currentTheme);
    this.overlayContainer.getContainerElement().classList.add(countryCode);
  }

  public getCurrentCssVariable(name: string): string {
    const style = window.getComputedStyle(this.document.documentElement);
    return style.getPropertyValue(name);
  }

  public loadStyle(id: string) {
    const head = this.document.getElementsByTagName('head')[0];
    const style = this.document.createElement('link');
    const meta = this.document.querySelector("meta[name='version']");
    const version = meta ? meta.getAttribute('content') : new Date().getTime();

    style.id = id;
    style.rel = 'stylesheet';
    style.href = `${id}.css?${version}`;

    head.appendChild(style);
  }

  public appendCustomVariables(variables: { type: 'color' | 'file'; value: string; name: string }[]) {
    const styleId = 'custom-style';
    let customStyleEl = this.document.querySelector(`style[id='${styleId}']`);

    if (!customStyleEl) {
      const headEl = this.document.querySelector('head');

      customStyleEl = this.document.createElement('style');
      customStyleEl.id = styleId;
      applyNonceToElement(customStyleEl as HTMLElement);

      headEl.appendChild(customStyleEl);
    }
    const stringifiedVariables = variables.reduce((acc, curr) => {
      const value = curr.type === 'file' ? `url(${curr.value})` : curr.value;
      return acc + `--${curr.name}:${value};`;
    }, '');
    const content = `.${this.currentTheme}{${stringifiedVariables}}`;

    customStyleEl.textContent = content;
  }

  public setCssTheme(response: string) {
    if (!response) {
      return;
    }
    const styles: string = response;
    const style: HTMLStyleElement = this.document.createElement('style');
    style.type = 'text/css';
    style.id = 'injectedThemeVariables';
    applyNonceToElement(style);
    this.document.head.appendChild(style);

    const materialIcons = `* .material-icons {
      font-family: 'Material Icons'!important;
  }`;

    style.appendChild(this.document.createTextNode(styles));
    style.appendChild(this.document.createTextNode(materialIcons));
  }
}
